import { isAfter } from "date-fns";
import { dateUtcIsBefore, parseDate } from "helpers";
import {
  CampaignRunState,
  CampaignRunStateServer,
  ICampaignServer,
  ICampaignStatusServer,
} from "model/campaigns";
import {
  ILineitem,
  ILineitemStatusServer,
  LineitemRunningStateServer,
} from "model/lineitems";
import {
  getActiveCampaigns,
  getActiveCampaignsObject,
  getAllCampaigns,
} from "redux/campaigns/selectors";
import store from "redux/store";
import { createDeepEqualSelector } from "redux/utils";
import { createSelector } from "reselect";
import { getCampaignsStatuses } from "./statusServerSelectors";

export const calcCampaignRunState = (
  campaignStatus: ICampaignStatusServer,
  campaign: ICampaignServer
): CampaignRunState => {
  const today = new Date();
  const runState = campaignStatus.runState;
  const lineitemsStatuses: Array<ILineitemStatusServer> =
    campaignStatus?.lineitems ?? [];
  const lineitems: Array<ILineitem> = campaign?.lineitems ?? [];
  const { Running: RunningRunState } = LineitemRunningStateServer;

  const allLineitemsPaused = !lineitemsStatuses.some(
    (l) => l?.lineitemState?.runState === RunningRunState
  );
  const allLineitemsFinished = !lineitems.some((l) =>
    isAfter(parseDate(l.finish) as Date, today)
  );

  if (!campaign?.landings?.length || !campaign?.lineitems?.length) {
    return CampaignRunState.Draft;
  }

  const { Running, Stopped } = CampaignRunStateServer;

  if (
    campaign.endDate &&
    !dateUtcIsBefore(new Date(), new Date(campaign.endDate))
  ) {
    return CampaignRunState.Finished;
  }

  if (allLineitemsFinished && runState === Running) {
    return CampaignRunState.RunningButFinished;
  }
  if (allLineitemsFinished && runState === Stopped) {
    return CampaignRunState.Finished;
  }
  if (runState === Stopped) {
    return CampaignRunState.Paused;
  }
  if (allLineitemsPaused && runState === Running) {
    return CampaignRunState.RunningButPaused;
  }
  if (runState === Running) {
    return CampaignRunState.Running;
  }

  return CampaignRunState.Unknown;
};

const calcCampaignListRunStates = (
  statuses: { [campaignId: string]: ICampaignStatusServer },
  campaigns
): { [campaignId: string]: CampaignRunState } => {
  return campaigns.reduce((acc, c) => {
    acc[c.id] = calcCampaignRunState(statuses?.[c.id] ?? {}, c);
    return acc;
  }, {});
};

export const getCampaignListRunStates = createSelector(
  getCampaignsStatuses,
  getAllCampaigns,
  (statuses, campaigns) => calcCampaignListRunStates(statuses, campaigns)
);

export const makeGetCampaignRunState = (campaignId) =>
  createSelector(
    getCampaignListRunStates,
    (runStates) => runStates[campaignId]
  );

export const getRunningCampaignsList = createDeepEqualSelector(
  getActiveCampaigns,
  getCampaignListRunStates,
  (campaignsList, runStatesObject) =>
    campaignsList.filter(
      (c) => runStatesObject[c.id] === CampaignRunState.Running
    )
);

export const getRunningCampaignsObject = createDeepEqualSelector(
  getActiveCampaignsObject,
  getCampaignListRunStates,
  (
    campaignsObject: { [key: string]: ICampaignServer },
    runStatesObject: { [key: string]: CampaignRunState }
  ) => {
    if (!campaignsObject) {
      return {};
    }

    return Object.values(campaignsObject).reduce((acc, c) => {
      if (runStatesObject[c.id] === CampaignRunState.Running) acc[c.id] = c;
      return acc;
    }, {});
  }
);

export const getCampaignSpent = (campaignId: string) =>
  store.getState().campaignsStatus.data[campaignId]?.spent || 0;

export const isCampaignDeletable = createSelector(
  //input
  [(state, campaignId) => campaignId, getCampaignsStatuses],
  //output
  (campaignId: string, statuses) => {
    if (!statuses?.[campaignId]) {
      return false;
    }
    const { runState, ...campStatusValues } = statuses[campaignId];
    return (campStatusValues as any)?.removable !== false;
  }
);
